// followStore.js — per-user follow-state persistence (Supabase `followed_assets`).
// Phase 3.3. Wraps the EXISTING anon supabaseClient; the front end reads/writes only
// its own rows (RLS: followed_assets_self_all, user_id = auth.uid()). Replaces the old
// localStorage follow-state. Cap-enforcement (profiles.tier) is NOT here — that is 3.3b.
import { supabase } from "./supabaseClient";

// SELECT every followed_assets row for the user. Throws on error (callers do NOT fall
// back to localStorage — a missing relation / permission error must surface, not hide).
export async function fetchFollowedAssets(userId) {
  const { data, error } = await supabase
    .from("followed_assets")
    .select("*")
    .eq("user_id", userId);
  if (error) throw error;
  return data ?? [];
}

// Add (or re-add) a followed asset. Upsert on (user_id, symbol) so a re-add is
// idempotent; is_followed reset to true.
export async function insertFollow(userId, symbol, assetClass) {
  const { error } = await supabase
    .from("followed_assets")
    .upsert(
      { user_id: userId, symbol, asset_class: assetClass, is_followed: true },
      { onConflict: "user_id,symbol" }
    );
  if (error) throw error;
}

// Remove a followed asset entirely (DELETE the row).
export async function deleteFollow(userId, symbol) {
  const { error } = await supabase
    .from("followed_assets")
    .delete()
    .eq("user_id", userId)
    .eq("symbol", symbol);
  if (error) throw error;
}

// Soft-dismiss a signal from the user's Notification History. Upsert on (user_id, signal_id)
// so a repeat dismiss is idempotent. Filtered server-side in get_my_notifications (GATE 4);
// the global signals row is NEVER touched. Throws on error (caller reverts its optimistic hide).
export async function dismissSignal(userId, signalId) {
  const { error } = await supabase
    .from("dismissed_signals")
    .upsert(
      { user_id: userId, signal_id: signalId },
      { onConflict: "user_id,signal_id" }
    );
  if (error) throw error;
}

// Flip the pause/unpause flag on an existing row.
export async function setFollowFlag(userId, symbol, isFollowed) {
  const { error } = await supabase
    .from("followed_assets")
    .update({ is_followed: isFollowed })
    .eq("user_id", userId)
    .eq("symbol", symbol);
  if (error) throw error;
}

// Clean-break seed: bulk-insert the code defaults, all followed, in one round trip.
// Called ONLY when the user's row set is genuinely empty.
export async function seedDefaults(userId, cryptoList, stockList) {
  const rows = [
    ...cryptoList.map(symbol => ({ user_id: userId, symbol, asset_class: "crypto", is_followed: true })),
    ...stockList.map(symbol => ({ user_id: userId, symbol, asset_class: "stock", is_followed: true })),
  ];
  const { error } = await supabase
    .from("followed_assets")
    .upsert(rows, { onConflict: "user_id,symbol" });
  if (error) throw error;
}
